import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { map, Observable, of, throwError } from "rxjs";
import {
  Signature,
  SaveSignatureRequest,
  Template,
  SignaturePredefined,
  SaveTemplatePredefined,
  InstallationResponse,
} from "./signatures.interface";
import { Planned } from "../planned/planned.interface";

export const wyswigConfig = {
  editable: true,
  sanitize: false,
  toolbarHiddenButtons: [["insertImage", "insertVideo"]],
};

@Injectable({ providedIn: "root" })
export class SignaturesService {
  constructor(private http: HttpClient) {}

  getSignatures = (all: boolean = false): Observable<Signature[]> => {
    const endpoint = all ? "/template" : "/template/preview";
    return this.http.get<Signature[]>(endpoint);
  };

  getSignatureWrappers = (): Observable<Signature[]> => {
    return this.http.get<Signature[]>("/template/wrappers");
  };

  getSignatureContent = (ids: string[]): Observable<Signature[]> => {
    return this.http.post<Signature[]>("/template/content", { ids });
  };

  getLazyLoadingTemplates = (
    offset: number = 0,
    limit: number = 30,
  ): Observable<Signature[]> => {
    const params = new HttpParams()
      .set("offset", offset.toString())
      .set("limit", limit.toString());
    return this.http.get<Signature[]>("/template/lazy-loading", { params });
  };

  changeSignature = (): Observable<Record<string, never>> => of({});

  removeSignature = (templateId: string): Observable<Record<string, never>> => {
    return this.http.delete<Record<string, never>>(`/template/${templateId}`);
  };

  duplicateSignature = (
    templateId: string,
  ): Observable<{ templateId: string }> => {
    return this.http.post<{ templateId: string }>(
      `/template/${templateId}/duplicate`,
      {},
    );
  };

  installSignature = (
    body: Record<string, unknown>,
  ): Observable<InstallationResponse> => {
    return this.http.post<InstallationResponse>("/footer", body);
  };

  uninstallSignature = (): Observable<Record<string, never>> => of({});

  reinstallSignatures = (): Observable<Record<string, never>> => {
    return this.http.post<Record<string, never>>("/template/reinstall", {});
  };

  setReinstallSchedule(
    enabled: boolean,
    time: string,
    domain: string,
  ): Observable<Record<string, never>> {
    return this.http.post<Record<string, never>>("/schedule/reinstall", {
      enabled,
      time,
      domain,
    });
  }

  reorderSignatures(orderedIds: string[]): Observable<Record<string, never>> {
    return this.http.put<Record<string, never>>(
      "/template/reorder",
      orderedIds,
    );
  }

  getAll = () => this.http.get<Template[]>("/template");
  save = (data: SaveSignatureRequest, installSignature: boolean = false) =>
    this.http.post<{ templateId: string }>("/template", {
      ...data,
      installSignature,
    });
  setDefaultForNewEmployee = (templateId: string) =>
    this.http.post("/template/default-for-new-employee", { templateId });
  byId = (templateId: string) => {
    const id = templateId?.trim() || "";
    if (!id || id === "undefined" || id === "null") {
      return throwError(() => new Error("templateId required"));
    }
    return this.http.get<Signature>(`/template/${id}`);
  };
  delete = (templateId: string) => {
    const id = templateId?.trim() || "";
    if (!id || id === "undefined" || id === "null") {
      return throwError(() => new Error("templateId required"));
    }
    return this.http.delete(`/template/${id}`);
  };
  placeholders = () => this.http.get(`/template/placeholders`);

  private static isContactVariable(name: string): boolean {
    const lower = name.toLowerCase();
    return (
      lower.includes("phone") ||
      lower.includes("mobile") ||
      lower.includes("email")
    );
  }

  getCustomLabelVariableOptions(): Observable<string[]> {
    return this.http
      .get<{ customLabelVariableOptions?: string[] }>("/template/placeholders")
      .pipe(
        map((res) => {
          const all = res?.customLabelVariableOptions ?? [];
          return all.filter(
            (name) => !SignaturesService.isContactVariable(name),
          );
        }),
      );
  }
  update = (
    templateId: string,
    content: SaveSignatureRequest,
    installSignature: boolean,
  ) =>
    this.http.put(`/template/${templateId}`, {
      ...content,
      installSignature,
    });
  predefined = () => this.http.get<SignaturePredefined[]>(`/predefined/`);

  getScheduled = () => this.http.get<Planned[]>(`/schedule`);
  removeScheduled = (templateCronId: string) =>
    this.http.delete(`/schedule/${templateCronId}`);
  setFooter = (body: Record<string, unknown>) =>
    this.http.post<Record<string, never>>(`/schedule/template`, body);

  getPredefined = () => this.http.get<SignaturePredefined[]>("/predefined");
  addPredefined = (
    predefined: SaveTemplatePredefined,
  ): Observable<SignaturePredefined> =>
    this.http.post<SignaturePredefined>("/admin/predefined", predefined);
  predefinedById = (
    templatePredefinedId: string,
  ): Observable<SignaturePredefined> =>
    this.http.get<SignaturePredefined>(`/predefined/${templatePredefinedId}`);
  updatePredefined = (
    templatePredefinedId: string,
    content: SaveTemplatePredefined,
  ) => this.http.put(`/admin/predefined/${templatePredefinedId}`, content);
  copyPredefinedToClient = (templatePredefinedId: string) =>
    this.http.post<{ templateId: string }>(
      `/predefined/${templatePredefinedId}/copy`,
      {},
    );
  deletePredefined = (templatePredefinedId: string) =>
    this.http.delete(`/admin/predefined/${templatePredefinedId}`);
  reorderPredefined = (idsOrder: string[]) =>
    this.http.put(`/admin/predefined`, idsOrder);

  preview(data: Record<string, unknown>): Observable<string> {
    return this.http.post("/template/preview", data, { responseType: "text" });
  }

  installDomainRules(
    templateId: string,
    data: Record<string, unknown>,
  ): Observable<Record<string, never>> {
    return this.http.post<Record<string, never>>(
      `/template/${templateId}/install-domain-rules`,
      data,
    );
  }
}
